/**
 * Which side of the M&A Deal Tracker a user is on. A Banker edits their own
 * tracker (TAD-6037); a Staffer reviews and edits their team's (TAD-6072/6073).
 */
export type DealTrackerRole = "banker" | "staffer";

/**
 * Sub-department names that put an M&A Junior Banker in front of their own Deal
 * Tracker. "M&A/Junior Banker View" is the name Triyo.API matches on when it
 * resolves the M&A widget; the second entry is the legacy `Department` string
 * the same users carry on older configurations.
 */
export const DEAL_TRACKER_SUB_DEPARTMENTS = ["M&A/Junior Banker View", "merger"];

/**
 * Sub-department names for the M&A Staffer. "Mergers Manager" is what Triyo.API
 * matches on for the team endpoint; "mergers-manager" is the legacy alias.
 */
export const DEAL_TRACKER_STAFFER_SUB_DEPARTMENTS = ["Mergers Manager", "mergers-manager"];

/** Sub-departments that see every widget in the tenant. */
const ALL_WIDGET_SUB_DEPARTMENTS = ["all", "allrbc"];

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function matches(names: Set<string>, aliases: string[]): boolean {
  return aliases.some((alias) => names.has(normalize(alias)));
}

/**
 * The Deal Tracker role implied by the caller's sub-departments, from
 * /dashboard/GetUserSubDepartments. Access is confirmed a second time by the API,
 * which serves no tracker and no team to an account that has none — this only
 * decides which surface is worth opening.
 *
 * Staffer wins when an account somehow carries both: it is the surface that can
 * reach every tracker the user is entitled to, including their own.
 */
export function resolveDealTrackerRole(subDepartments: string[]): DealTrackerRole | null {
  const names = new Set(subDepartments.filter(Boolean).map(normalize));

  if (matches(names, DEAL_TRACKER_STAFFER_SUB_DEPARTMENTS)) return "staffer";
  if (matches(names, DEAL_TRACKER_SUB_DEPARTMENTS)) return "banker";
  // A tenant-wide dashboard account has no team of its own to staff; the Banker
  // surface is what the API will actually serve them.
  if (matches(names, ALL_WIDGET_SUB_DEPARTMENTS)) return "banker";
  return null;
}

/** Whether the Deal Tracker quick action is offered at all. */
export function canUseDealTracker(subDepartments: string[]): boolean {
  return resolveDealTrackerRole(subDepartments) !== null;
}
